<app-component-level-error-message [errorMessage]="errorMessage"></app-component-level-error-message>
<div class="ticket-type-header margin-top-large">
  <div class="justify-space-between flex-row align-start">
    <div>
      <div class="section-header">Lottery License Report</div>
      <div>Date Range:</div>
      {{ reportingData.StartDate | date: 'MMM d, y':'UTC' }} -
      {{ reportingData.EndDate | date: 'MMM d, y':'UTC' }}
    </div>
    <div>
      <button mat-raised-button color="primary" (click)="generatePDF()"
              data-testid="comprehensive-report-generate-pdf-button">Generate PDF
      </button>
    </div>
  </div>

</div>

<h3>Lottery License Report</h3>
@if (uiState === uiStateForTemplate.ShowLoading) {
  <app-loading-card-content></app-loading-card-content>
} @else if (uiState === uiStateForTemplate.ShowData) {

  <div class="margin-left-med flex-row-wrap justify-space-between align-start gap-normal">
    <div class="flex-report-item bottom-border">
      <div class="report-label">Game Name</div>
      <div class="report-value" data-testid="lottery-licence-report-game-name">{{ lotteryLicenseReport.GameName }}</div>
    </div>
    <div class="flex-report-item bottom-border">
      <div class="report-label">Game Type</div>
      <div class="report-value"
           data-testid="lottery-licence-report-game-type">{{ lotteryLicenseReport.GameType | gameType }}
      </div>
    </div>
    <div class="flex-report-item">
      <div class="report-label">Tickets Sold</div>
      <div class="report-value"
           data-testid="lottery-licence-report-tickets-sold">{{ lotteryLicenseReport.TicketsSold }}
      </div>
    </div>
    <div class="flex-report-item bottom-border">
      <div class="report-label">Total Gross Sales</div>
      <div class="report-value"
           data-testid="lottery-licence-report-total-gross-sales">{{ lotteryLicenseReport.TotalGrossSales | formatCurrency }}
      </div>
    </div>
    <div class="flex-report-item bottom-border">
      <div class="report-label">Total Expenses</div>
      <div class="report-value"
           data-testid="lottery-licence-report-total-expenses">{{ lotteryLicenseReport.TotalExpenses | formatCurrency }}
      </div>
    </div>
    <div class="flex-report-item bottom-border">
      <div class="report-label">Net Proceeds Raised for Charity</div>
      <div class="report-value"
           data-testid="lottery-licence-report-net-proceeds">{{ lotteryLicenseReport.NetProceedsForCharity | formatCurrency }}
      </div>
    </div>
  </div>

  <h3 class="winner-subtitle">Prize Pot Reconciliation</h3>
  <div class="margin-left-med reconciliation">
    <!--
      #1517: this used to be two figures side by side - "awarded to winners" and "allocated to player
      winnings" - with footnotes trying to explain why they disagree. They disagree because a pot does
      not start or finish a period empty, and a reader cannot check that from two numbers. Showing the
      whole movement means the period adds up on the page and an auditor can follow it.
    -->
    <div class="flex-row justify-space-between align-center reconciliation-row">
      <div class="report-label">Pot at the start of the period <small>(rolled over from the previous period, or the seed for a game's first draw)</small></div>
      <div class="report-value"
           data-testid="lottery-licence-report-pot-carried-in">{{ lotteryLicenseReport.PotCarriedIn | formatCurrency }}
      </div>
    </div>
    <div class="flex-row justify-space-between align-center reconciliation-row">
      <div class="report-label">Allocated to player winnings this period <small>(approximately half of gross sales; each payment's share is rounded up)</small></div>
      <div class="report-value"
           data-testid="lottery-licence-report-amount-allocated-to-prizes">{{ lotteryLicenseReport.AmountAllocatedToPlayerWinnings | formatCurrency }}
      </div>
    </div>
    @if (lotteryLicenseReport.PotAdjustments !== 0) {
      <div class="flex-row justify-space-between align-center reconciliation-row">
        <div class="report-label">Seeds, refunds and other pot adjustments</div>
        <div class="report-value"
             data-testid="lottery-licence-report-pot-adjustments">{{ lotteryLicenseReport.PotAdjustments | formatCurrency }}
        </div>
      </div>
    }
    <div class="flex-row justify-space-between align-center reconciliation-row reconciliation-total">
      <div class="report-label">Total amount awarded to winners</div>
      <div class="report-value"
           data-testid="lottery-licence-report-total-prizes-awarded">{{ lotteryLicenseReport.TotalPrizesAwarded | formatCurrency }}
      </div>
    </div>
    <div class="flex-row justify-space-between align-center reconciliation-row">
      <div class="report-label">Pot carried forward to the next period <small>(drawn but unclaimed)</small></div>
      <div class="report-value"
           data-testid="lottery-licence-report-pot-carried-forward">{{ lotteryLicenseReport.PotCarriedForward | formatCurrency }}
      </div>
    </div>
    <div class="reconciliation-note">
      Start of period + allocated + adjustments = awarded + carried forward. Only draws that have been
      held are included, and a draw belongs to the period its scheduled end date falls in. A period is
      therefore not final until every draw scheduled in it has been held &mdash; re-running this report
      after a late draw completes will not match a copy filed before it.
    </div>
  </div>

  <h3 class="winner-subtitle">Licence Number</h3>
  <div>
    <mat-list data-testid="lottery-license-report-licence-list">
        <mat-list-item class="padding-bottom-med padding-top-med license-section">
          <span matListItemTitle data-testid="license-number">{{ lotteryLicenseReport.LicenceNumber.LicenceNumber }}</span>
          <span matLine data-testid="license-status">Status: {{ lotteryLicenseReport.LicenceNumber.Active ? 'Active' : 'Expired' }}</span>
          <span matLine data-testid="license-start-date">Start Date: {{ lotteryLicenseReport.LicenceNumber.StartDate | date: 'yyyy-MM-dd' }}</span>
          <span data-testid="license-end-date"
                matLine>End Date: {{ lotteryLicenseReport.LicenceNumber.EndDate ? (lotteryLicenseReport.LicenceNumber.EndDate| date: 'yyyy-MM-dd') : 'No End Date' }}</span>
        </mat-list-item>
    </mat-list>
  </div>

  <h3 class="winner-subtitle">Winners</h3>
  <div>
    <mat-list data-testid="lottery-license-report-winner-list">
      @for (winner of lotteryLicenseReport.Winners; track winner) {
        <mat-list-item class="padding-bottom-med padding-top-med winner-section">
          <span matListItemTitle data-testid="winner-name">{{ winner.FirstName }} {{ winner.LastName }}</span>
          <span matLine data-testid="winner-phone">Phone: {{ winner.Phone }}</span>
          <span matLine data-testid="winner-winnings-amount">Winnings: {{ winner.PlayerWinnings | formatCurrency }}</span>
          <span matLine data-testid="winner-draw-date">Draw date: {{ winner.EndedOn | date: 'yyyy-MM-dd' }}</span>
        </mat-list-item>
      }
    </mat-list>
  </div>
}

